import { supabase } from '@/lib/supabase';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { UserSubscription, AIReviewUsage } from '@/types/subscription';

export interface ExtendedSubscription extends UserSubscription {
  ai_reviews_purchased?: number;
  total_reviews_purchased?: number;
}

export const fetchUserSubscription = async (userId: string): Promise<ExtendedSubscription | null> => {
  try {
    const { data, error } = await supabase.rpc('get_subscription_with_reset', {
      user_id_param: userId
    });
    
    if (error) {
      adminError('Error fetching subscription:', error);
      return null;
    }
    
    // RPC returns an array (SETOF), take the first row
    const row = Array.isArray(data) ? data[0] : data;
    return row ? (row as ExtendedSubscription) : null;
  } catch (error) {
    adminError('Error in fetchUserSubscription:', error);
    return null;
  }
};

export interface ExtendedAIReviewUsage extends AIReviewUsage {
  tier: string;
  periodEnd: string;
  purchasedCredits: number;
  totalPurchased: number;
  freeRemaining: number;
}

export const getAIReviewUsage = async (userId: string): Promise<ExtendedAIReviewUsage> => {
  const subscription = await fetchUserSubscription(userId);
  
  if (!subscription) {
    return {
      used: 0,
      limit: 3,
      remaining: 3,
      canRequestMore: true,
      percentUsed: 0,
      tier: 'free',
      periodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      purchasedCredits: 0,
      totalPurchased: 0,
      freeRemaining: 3,
    };
  }
  
  const freeRemaining = Math.max(0, subscription.ai_reviews_limit_monthly - subscription.ai_reviews_used_current_month);
  const purchasedCredits = subscription.ai_reviews_purchased || 0;
  const totalRemaining = freeRemaining + purchasedCredits;
  const percentUsed = subscription.ai_reviews_limit_monthly > 0 
    ? (subscription.ai_reviews_used_current_month / subscription.ai_reviews_limit_monthly) * 100 
    : 0;
  
  return {
    used: subscription.ai_reviews_used_current_month,
    limit: subscription.ai_reviews_limit_monthly,
    remaining: totalRemaining,
    canRequestMore: totalRemaining > 0,
    percentUsed,
    tier: subscription.tier,
    periodEnd: subscription.billing_period_end,
    purchasedCredits,
    totalPurchased: subscription.total_reviews_purchased || 0,
    freeRemaining,
  };
};

export const canRequestAIReview = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.rpc('can_request_ai_review', {
      user_id_param: userId
    });
    
    if (error) {
      adminError('Error checking AI review availability:', error);
      return false;
    }
    
    return data as boolean;
  } catch (error) {
    adminError('Error in canRequestAIReview:', error);
    return false;
  }
};

export const incrementAIReviewUsage = async (userId: string): Promise<boolean> => {
  try {
    // Use the new consume function that handles both free and purchased credits
    const { data, error } = await supabase.rpc('consume_ai_review_credit', {
      user_id_param: userId
    });
    
    if (error) {
      adminError('Error consuming AI review credit:', error);
      return false;
    }
    
    return data as boolean;
  } catch (error) {
    adminError('Error in incrementAIReviewUsage:', error);
    return false;
  }
};

export interface CreditPurchase {
  id: string;
  pack_type: string;
  credits_amount: number;
  price_paid: number;
  currency: string;
  status: string;
  created_at: string;
}

export const fetchPurchaseHistory = async (userId: string): Promise<CreditPurchase[]> => {
  try {
    const { data, error } = await supabase
      .from('credit_purchases')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      adminError('Error fetching purchase history:', error);
      return [];
    }
    
    return data as CreditPurchase[];
  } catch (error) {
    adminError('Error in fetchPurchaseHistory:', error);
    return [];
  }
};

export const makePaperPublic = async (paperId: string): Promise<boolean> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      adminError('User not authenticated');
      return false;
    }

    const { data, error } = await supabase.rpc('make_paper_public', {
      paper_id_param: paperId,
      user_id_param: user.id
    });

    if (error) {
      adminError('Error making paper public:', error);
      return false;
    }

    adminLog('Paper made public successfully:', paperId);
    return data as boolean;
  } catch (error) {
    adminError('Error in makePaperPublic:', error);
    return false;
  }
};
